import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useRouter } from 'next/router';

import {
  getPlatformMe,
  IPlatformAuthLogin,
  IPlatformAuthUser,
  platformAuthLogin,
  platformRefreshToken,
} from '../services/controllers/platform-auth';
import { IFCWithChildren } from '../interfaces/i-fc-with-children';
import { useCookies } from '../hooks/use-cookies';
import { cookies } from '../constants/cookies';

interface IPlatformAuthContextData {
  user: IPlatformAuthUser | null;
  isLoading: boolean;
  login: (payload: IPlatformAuthLogin) => Promise<void>;
  logout: () => void;
}

const PlatformAuthContext = createContext({} as IPlatformAuthContextData);

const REFRESH_TOKEN_BEFORE_EXPIRATION_MS = 2 * 60 * 1000;

const getTokenExpiresAt = (token?: string): number | null => {
  if (!token || typeof window === 'undefined') return null;

  try {
    const [, payload] = token.split('.');
    const decoded = JSON.parse(window.atob(payload));

    return decoded.exp ? decoded.exp * 1000 : null;
  } catch (error) {
    return null;
  }
};

export const PlatformAuthProvider: IFCWithChildren = ({ children }) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { getItem, setItem, removeItem } = useCookies();
  const [user, setUser] = useState<IPlatformAuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoggingOutRef = useRef(false);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    isLoggingOutRef.current = true;
    clearRefreshTimer();
    void queryClient.cancelQueries({
      predicate: ({ queryKey }) =>
        typeof queryKey[0] === 'string' && queryKey[0].startsWith('platform-'),
    });
    queryClient.removeQueries({
      predicate: ({ queryKey }) =>
        typeof queryKey[0] === 'string' && queryKey[0].startsWith('platform-'),
    });
    setIsLoading(false);
    setUser(null);
    removeItem(cookies.PLATFORM_AUTH_TOKEN);
    removeItem(cookies.PLATFORM_REFRESH_TOKEN);

    if (router.asPath !== '/admin') {
      if (typeof window !== 'undefined') {
        window.location.replace('/admin');
        return;
      }

      void router.replace('/admin');
    }
  }, [clearRefreshTimer, queryClient, removeItem, router]);

  const scheduleRefresh = useCallback(
    (accessToken?: string) => {
      clearRefreshTimer();

      const expiresAt = getTokenExpiresAt(accessToken);
      if (!expiresAt) return;

      const refreshIn =
        expiresAt - Date.now() - REFRESH_TOKEN_BEFORE_EXPIRATION_MS;

      refreshTimeoutRef.current = setTimeout(async () => {
        const currentRefreshToken = getItem(cookies.PLATFORM_REFRESH_TOKEN);

        if (!currentRefreshToken) {
          logout();
          return;
        }

        try {
          const response = await platformRefreshToken(currentRefreshToken);
          setItem(cookies.PLATFORM_AUTH_TOKEN, response.accessToken);
          setItem(cookies.PLATFORM_REFRESH_TOKEN, response.refreshToken);
          setUser(response.user);
          scheduleRefresh(response.accessToken);
        } catch (error) {
          logout();
        }
      }, Math.max(refreshIn, 0));
    },
    [clearRefreshTimer, getItem, logout, setItem]
  );

  const login = useCallback(
    async (payload: IPlatformAuthLogin) => {
      isLoggingOutRef.current = false;
      const { data } = await platformAuthLogin(payload);
      setItem(cookies.PLATFORM_AUTH_TOKEN, data.accessToken);
      setItem(cookies.PLATFORM_REFRESH_TOKEN, data.refreshToken);
      setUser(data.user);
      scheduleRefresh(data.accessToken);
      await router.push('/admin/painel');
    },
    [router, scheduleRefresh, setItem]
  );

  useEffect(() => {
    const boot = async (): Promise<void> => {
      if (isLoggingOutRef.current) {
        setIsLoading(false);
        return;
      }

      const isAdminRoute = router.asPath.startsWith('/admin');
      if (!isAdminRoute) {
        setIsLoading(false);
        return;
      }

      const accessToken = getItem(cookies.PLATFORM_AUTH_TOKEN);
      const refreshToken = getItem(cookies.PLATFORM_REFRESH_TOKEN);

      if (!accessToken && !refreshToken) {
        setIsLoading(false);
        return;
      }

      try {
        if (accessToken) {
          const me = await getPlatformMe(accessToken);
          isLoggingOutRef.current = false;
          setUser(me);
          scheduleRefresh(accessToken);
          setIsLoading(false);
          return;
        }

        const refreshed = await platformRefreshToken(refreshToken);
        isLoggingOutRef.current = false;
        setItem(cookies.PLATFORM_AUTH_TOKEN, refreshed.accessToken);
        setItem(cookies.PLATFORM_REFRESH_TOKEN, refreshed.refreshToken);
        setUser(refreshed.user);
        scheduleRefresh(refreshed.accessToken);
      } catch (error) {
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    boot();
  }, [getItem, logout, router.asPath, scheduleRefresh, setItem]);

  return (
    <PlatformAuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </PlatformAuthContext.Provider>
  );
};

export const usePlatformAuth = (): IPlatformAuthContextData =>
  useContext(PlatformAuthContext);
